'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { ArrowRight, MapPin, Zap, TrendingUp } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface ActiveIssue {
  name: string;
  market: string;
  regionsEnabled: string[];
}

interface RegionSummary {
  flCount: number;
  prCount: number;
  totalCount: number;
  cities: string[];
}

export default function HeroSection() {
  const [activeIssue, setActiveIssue] = useState<ActiveIssue | null>(null);
  const [regionSummary, setRegionSummary] = useState<RegionSummary>({
    flCount: 0,
    prCount: 0,
    totalCount: 0,
    cities: ['Tampa', 'Orlando', 'Miami', 'Jacksonville', 'Sarasota', 'Naples'],
  });

  useEffect(() => {
    const supabase = createClient();

    const fetchData = async () => {
      // Fetch the current booking issue
      const { data: issueData } = await supabase
        .from('issues')
        .select('issue_number, name, market, regions_enabled')
        .eq('status', 'booking')
        .order('issue_number', { ascending: true })
        .limit(1)
        .maybeSingle();

      // Fetch active regions for stats
      const { data: regionsData } = await supabase
        .from('regions')
        .select('id, name, market, cities')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (issueData) {
        setActiveIssue({
          name: issueData.name || `Issue #${issueData.issue_number}`,
          market: issueData.market,
          regionsEnabled: Array.isArray(issueData.regions_enabled) ? issueData.regions_enabled : [],
        });
      }

      if (regionsData?.length) {
        const fl = regionsData.filter((r: any) => r.market === 'FL');
        const pr = regionsData.filter((r: any) => r.market === 'PR');
        // Collect a sample of cities from FL regions for the mockup
        const sampleCities: string[] = [];
        for (const region of fl) {
          if (Array.isArray(region.cities)) {
            for (const city of region.cities) {
              if (sampleCities.length < 6) sampleCities.push(city);
            }
          }
        }
        setRegionSummary({
          flCount: fl.length,
          prCount: pr.length,
          totalCount: regionsData.length,
          cities: sampleCities.length >= 3 ? sampleCities : ['Tampa', 'Orlando', 'Miami', 'Jacksonville', 'Sarasota', 'Naples'],
        });
      }
    };

    void fetchData();
  }, []);

  const totalRegions = regionSummary.totalCount || 12;
  const eyebrowText = activeIssue
    ? `Now booking ${activeIssue.name} — Florida & Puerto Rico`
    : 'Now booking — Florida & Puerto Rico';

  return (
    <section className="relative min-h-screen flex items-center justify-center pt-16 pb-20 overflow-hidden">
      {/* Radial glow backgrounds */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-brand-600/10 blur-3xl" />
        <div className="absolute top-1/3 right-1/4 w-80 h-80 rounded-full bg-accent-blue/8 blur-3xl" />
        <div className="absolute bottom-1/4 left-1/2 w-64 h-64 rounded-full bg-accent-purple/8 blur-3xl" />
      </div>
      <div className="relative z-10 max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16">
        <div className="max-w-4xl mx-auto text-center">
          {/* Eyebrow */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card border border-brand-600/30 mb-8">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="text-sm font-medium text-brand-300">{eyebrowText}</span>
          </div>

          {/* Headline */}
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-900 leading-[1.05] tracking-tight mb-6">
            <span className="text-white">Get New Local</span>
            <br />
            <span className="text-white">Customers </span>
            <span className="text-gradient">Every 5 Weeks</span>
          </h1>

          <p className="text-lg sm:text-xl text-slate-400 leading-relaxed max-w-2xl mx-auto mb-10">
            ZipAdBook delivers 1,000 targeted coupon books per region straight to homes and businesses across Florida and Puerto Rico — printed, distributed, and tracked.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-14">
            <Link
              href="/order-wizard"
              className="flex items-center gap-2 px-7 py-3.5 text-base font-semibold btn-primary"
            >
              Book an Ad
              <ArrowRight size={18} />
            </Link>
            <Link
              href="/regions-pricing"
              className="flex items-center gap-2 px-7 py-3.5 text-base font-medium btn-outline"
            >
              <MapPin size={16} />
              View Regions & Pricing
            </Link>
          </div>

          {/* Stats strip */}
          <div className="grid grid-cols-3 gap-3 sm:gap-4 max-w-2xl mx-auto">
            {[
              { icon: MapPin, value: String(totalRegions), label: 'Regions Covered', color: 'text-brand-400' },
              { icon: TrendingUp, value: '1,000', label: 'Copies Per Region', color: 'text-accent-blue' },
              { icon: Zap, value: 'Every 5wk', label: 'New Issue Cycle', color: 'text-accent-purple' },
            ]?.map((stat) => (
              <div key={`hero-stat-${stat?.label}`} className="glass-card rounded-2xl p-3 sm:p-4 text-center">
                <stat.icon size={18} className={`${stat?.color} mx-auto mb-1.5 sm:mb-2`} />
                <p className={`font-display text-base sm:text-xl font-bold ${stat?.color} tabular-nums`}>{stat?.value}</p>
                <p className="text-[10px] sm:text-xs text-slate-500 mt-0.5 leading-tight">{stat?.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Book mockup visual */}
        <div className="mt-16 relative max-w-3xl mx-auto">
          <div className="glass-card rounded-3xl p-8 glow-primary">
            <div className="bg-gradient-to-br from-navy-600 to-navy-800 rounded-2xl aspect-[8.5/5.5] flex items-center justify-center border border-brand-600/20 relative overflow-hidden">
              <div className="absolute inset-0 grid-texture opacity-30" />
              <div className="relative z-10 text-center">
                <div className="inline-flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-gradient-primary flex items-center justify-center">
                    <Zap size={16} className="text-white" />
                  </div>
                  <span className="font-display font-bold text-lg text-white">ZipAdBook</span>
                </div>
                <p className="text-sm text-slate-400">
                  {activeIssue ? activeIssue.name : 'Current Issue'} • 8.5×5.5 • 1,000 Copies
                </p>
                <div className="flex items-center justify-center gap-2 mt-4 flex-wrap">
                  {regionSummary.cities?.map((city) => (
                    <span key={`mockup-chip-${city}`} className="chip text-xs">{city}</span>
                  ))}
                </div>
              </div>
              {/* Decorative page lines */}
              <div className="absolute bottom-4 left-6 right-6 space-y-1.5 opacity-20">
                {[70, 55, 40, 80, 60]?.map((w, i) => (
                  <div key={`line-${i}`} className="h-1.5 rounded-full bg-brand-400" style={{ width: `${w}%` }} />
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between mt-4 px-1">
              <span className="text-xs text-slate-500">12 pages • 9 sellable ad pages</span>
              <span className="chip">Landscape Format</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}